import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaEthereum, FaUserCircle, FaBars, FaTimes, FaMoon, FaSun } from "react-icons/fa";

// Placeholder for your logo - you'll need to replace this with your actual import
const EscroniaLogo = () => (
  <div className="text-4xl font-extrabold bg-gradient-to-r from-green-400 to-blue-500 text-transparent bg-clip-text">
    ESCROWNIA
  </div>
);

const Navbar = ({ account, network, connectWallet, disconnectWallet }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => document.body.classList.contains("dark"));
  const location = useLocation();

  useEffect(() => {
    // Add scroll listener
    const handleScroll = () => {
      const offset = window.scrollY;
      if (offset > 50) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    // Close mobile menu when route changes
    setIsOpen(false);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [location]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const toggleDarkMode = () => {
    if (document.body.classList.contains("dark")) {
      document.body.classList.remove("dark");
      setIsDarkMode(false);
    } else {
      document.body.classList.add("dark");
      setIsDarkMode(true);
    }
  };

  return (
    <header className={`fixed w-full z-30 transition-all duration-300 ${scrolled ? 'bg-gray-900 shadow-lg py-2' : 'bg-transparent py-4'}`}>
      <div className="container mx-auto px-6">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <EscroniaLogo />
          </Link>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8 items-center">
            <Link to="/how-to-use" className="text-gray-300 hover:text-white transition-colors">
              How It Works
            </Link>
            <Link to="/about" className="text-gray-300 hover:text-white transition-colors">
              About Us
            </Link>
            
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-full hover:bg-gray-700 transition-colors"
              aria-label="Toggle dark mode"
            >
              {isDarkMode ? (
                <FaSun className="text-yellow-300 h-5 w-5" />
              ) : (
                <FaMoon className="text-gray-300 h-5 w-5" />
              )}
            </button>
            
            {/* Wallet Connection */}
            {account ? (
              <div className="flex items-center">
                <p className="text-accent font-medium mr-2">
                  {account.slice(0, 6)}...{account.slice(-4)}
                </p>
                <span className="px-2 py-1 bg-blue-900 text-xs rounded-full text-blue-300">
                  {typeof network === 'object' ? network.name : network}
                </span>
                <button
                  onClick={disconnectWallet}
                  className="ml-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  Disconnect
                </button>
              </div>
            ) : (
              <button
                onClick={connectWallet}
                className="px-4 py-2 rounded-lg bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white transition-colors"
              >
                Connect Wallet
              </button>
            )}
          </nav>
          
          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-4">
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded-full hover:bg-gray-700 transition-colors"
              aria-label="Toggle dark mode"
            >
              {isDarkMode ? (
                <FaSun className="text-yellow-300 h-5 w-5" />
              ) : (
                <FaMoon className="text-gray-300 h-5 w-5" />
              )}
            </button>
            
            <button
              onClick={toggleMenu}
              className="text-white focus:outline-none"
              aria-label="Toggle menu"
            >
              {isOpen ? (
                <FaTimes className="h-6 w-6" />
              ) : (
                <FaBars className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Mobile Navigation */}
      <div
        className={`md:hidden bg-gray-900 overflow-hidden transition-all duration-300 ease-in-out ${
          isOpen ? 'max-h-96 py-4' : 'max-h-0'
        }`}
      >
        <div className="container mx-auto px-6 flex flex-col space-y-4">
          <Link
            to="/how-to-use"
            className="py-2 text-gray-300 hover:text-white border-b border-gray-800"
            onClick={() => setIsOpen(false)}
          >
            How It Works
          </Link>
          <Link
            to="/about"
            className="py-2 text-gray-300 hover:text-white border-b border-gray-800"
            onClick={() => setIsOpen(false)}
          >
            About Us
          </Link>
          
          {/* Wallet Connection Mobile */}
          <div className="pt-2">
            {account ? (
              <div className="flex flex-col space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-accent font-medium">
                    {account.slice(0, 6)}...{account.slice(-4)}
                  </p>
                  <span className="px-2 py-1 bg-blue-900 text-xs rounded-full text-blue-300">
                    {typeof network === 'object' ? network.name : network}
                  </span>
                </div>
                <button
                  onClick={disconnectWallet}
                  className="w-full py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                >
                  Disconnect
                </button>
              </div>
            ) : (
              <button
                onClick={connectWallet}
                className="w-full py-2 rounded-lg bg-gradient-to-r from-green-500 to-blue-600 hover:from-green-600 hover:to-blue-700 text-white transition-colors"
              >
                Connect Wallet
              </button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;